import { useAuth } from '@/contexts';
import { useClient } from './useClient';
import { ErrorI } from '@/types/cyclone/requests';
import { useMutation } from '@tanstack/react-query';

/**
 * Rechazos de la API que son la REGLA funcionando, no una falla: un profesional del equipo sin el
 * permiso para lo que intentó (403 `STAFF_PERMISSION_REQUIRED`, el único code de permiso que manda
 * cyclone). La pantalla le muestra el motivo a quien lo intentó; a #incidents sólo llegaba ruido.
 * Lista cerrada a propósito: cualquier otro error sigue avisando.
 */
const EXPECTED_DENIAL_CODES = ['STAFF_PERMISSION_REQUIRED'];

/** El mensaje llega armado (con `stringifyError` adentro) o, a veces, como el objeto del error. */
export const isExpectedDenial = (message: unknown): boolean => {
  if (message && typeof message === 'object') {
    return EXPECTED_DENIAL_CODES.includes(String((message as { code?: unknown }).code));
  }
  if (typeof message !== 'string') return false;
  return EXPECTED_DENIAL_CODES.some(
    (code) => message.includes(`"code":"${code}"`) || message.includes(`code: ${code}`)
  );
};

const useLog = () => {
  const { client } = useClient();
  const { session } = useAuth();

  const mutation = useMutation<any, ErrorI, { message: string }>({
    mutationFn: (params) =>
      client<any>(`slack`, 'POST', {
        isAuthRequired: true,
        data: params
      })
  });

  const logAndNotify = (message: any) => {
    if (isExpectedDenial(message)) return;
    if (import.meta.env.VITE_NODE_ENV == 'production') {
      mutation.mutate({
        message: `${session?.vendor?.username} (${session?.email}) tuvo un error - ${message}`
      });
    } else {
      console.log('Simulating error', message);
    }
  };

  return { logAndNotify };
};

export default useLog;
